import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NumericFormat } from 'react-number-format';
import { Layout } from './CoreComponents';
import ItemView from './Shared/ItemView';
import { formatDate } from '../utils/formatterDate';
import { useToast } from '../contexts/ToastContext';
import { setQuoteItemAwarded, setQuoteItemRejected, negotiateQuoteItem, fetchQuoteItemHistory, selectQuoteItemHistory, selectIsQuoteItemHistoryLoading } from '../store/slices/quoteSlice';
import { getPORequests, getPORequestQuoteItems, deletePORequestWithQuoteItems, getStatusCounts } from '../store/slices/poRequestSlice';
import { deleteQuoteItem } from '../store/slices/quoteItemSlice';
import { getItemPurchaseHistory } from '../store/slices/itemSlice';
import PriceInput from './Shared/PriceInput';
import ConfirmationModal from './Shared/ConfirmationModal';
import {
  ExchangeRatePrompt,
  ManualRateNote,
  needsRatePrompt,
  type ExchangeRateInfo,
  type ManualRates,
} from './Shared/ExchangeRatePrompt';
import { RootState, AppDispatch } from '../store/index';
import { useUser } from '../contexts/UserContext';
import { useLoading } from '../contexts/LoadingContext';
import { useExpandedBatches } from '../hooks/useExpandedBatches';
import { AgGridReact } from 'ag-grid-react';
import { type ColDef, type ICellRendererParams } from 'ag-grid-community';
import BaseGrid from './Shared/BaseGrid';

interface QuoteEvaluationPageProps {}

interface Offer {
  id: string;
  requestNo: string;
  requestDate: string;
  requestedDeliveryDate?: string;
  productCode: string;
  productName: string;
  rootCode?: string;
  shortCode: string;
  categoryName?: string;
  requestQuantity: string;
  unit?: string;
  inCart?: boolean;
  cartQuantity?: number;
  cartPrice?: number;
  cartSupplier?: string;
  decisionStatus?: string;
  relatedQuotes?: any[];
  itemId?: number;
  decisionUserId?: number | null;
  /** Teklif turu (getPORequestQuoteItems response'tan) */
  round?: number | null;
  createdByName?: string;
}


/**
 * Award/Reject cevabindan checkout'un gerceklesip gerceklesmedigini okur.
 * Backend ApiResponse.data icinde { checkedOut: bool } donduruyor.
 */
const didCheckOut = (result: unknown): boolean => {
  const response = (result as { response?: { data?: { checkedOut?: boolean } } })?.response;
  return response?.data?.checkedOut === true;
};

const QuoteEvaluationPage = (_props: QuoteEvaluationPageProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { showSuccess, showWarning, showError } = useToast();
  const { user } = useUser();
  const { showLoading, hideLoading } = useLoading();
  const gridRef = useRef<AgGridReact>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [sortBy, setSortBy] = useState('delivery-date-desc');
  const [showModal, setShowModal] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState<Offer | null>(null);
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [selectedSuppliers, setSelectedSuppliers] = useState<string[]>([]);
  const [isMuzakere, setIsMuzakere] = useState(false);
  const [isLoadingQuoteItems, setIsLoadingQuoteItems] = useState(false);
  // Netsis'te bugünün kuru yoksa kullanıcının o açılış için girdiği kurlar (kaydedilmez)
  const [manualRates, setManualRates] = useState<ManualRates>({});
  const [exchangeRateInfo, setExchangeRateInfo] = useState<ExchangeRateInfo | null>(null);
  const [ratePrompt, setRatePrompt] = useState<{ offer: Offer; quotes: any[]; info: ExchangeRateInfo } | null>(null);
  const [showItemViewModal, setShowItemViewModal] = useState(false);
  const [itemToView, setItemToView] = useState<any>(null);
  const [expandedSuppliers, setExpandedSuppliers] = useState<Set<string>>(new Set());
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedQuoteItemId, setSelectedQuoteItemId] = useState<number | null>(null);
  const [selectedQuoteItemSupplierName, setSelectedQuoteItemSupplierName] = useState<string>('');
  const [expandedBatches, setExpandedBatches] = useExpandedBatches('quoteEvaluation_expandedBatches');
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [offerToDelete, setOfferToDelete] = useState<Offer | null>(null);
  const [showQuoteItemDeleteConfirmation, setShowQuoteItemDeleteConfirmation] = useState(false);
  const [quoteItemToDelete, setQuoteItemToDelete] = useState<{ id: number; supplierName: string } | null>(null);
  const [isSavingSelection, setIsSavingSelection] = useState(false);

  // Helper function to get current user ID
  const getCurrentUserId = useCallback(() => {
    if (user?.id) {
      console.log('Using user ID from Redux state:', user.id);
      return user.id;
    }
    // Fallback to localStorage if Redux state is not available
    try {
      const currentUser = localStorage.getItem('currentUser');
      if (currentUser) {
        const userData = JSON.parse(currentUser);
        console.log('Using user ID from localStorage:', userData.id);
        return userData.id;
      }
    } catch (error) {
      console.warn('Error parsing currentUser from localStorage:', error);
    }
    console.warn('No user ID found');
    return undefined;
  }, [user?.id]);
  
  // Müzakere modu input değerleri
  const [negotiateValues, setNegotiateValues] = useState<{[key: string]: {
    targetQuantity?: number;
    targetPrice?: number;
    targetDeliveryDays?: number;
  }}>({});

  // Önizleme modu: decisionUserId !== currentUserId ise kaydet/seçim vb. çalışmaz
  const isPreviewMode = Boolean(
    selectedOffer &&
    selectedOffer.decisionUserId != null &&
    getCurrentUserId() !== undefined &&
    selectedOffer.decisionUserId !== getCurrentUserId()
  );

  // Redux state for PO Requests
  const poRequests = useSelector((state: RootState) => state.poRequest.items);
  const isLoading = useSelector((state: RootState) => state.poRequest.loading);
  const error = useSelector((state: RootState) => state.poRequest.error);
  
  // Item purchase history state
  const purchaseHistory = useSelector((state: RootState) => state.item.purchaseHistory);
  const isPurchaseHistoryLoading = useSelector((state: RootState) => state.item.operationLoading.purchaseHistory);
  
  // Quote items state
  const isQuoteItemsLoading = useSelector((state: RootState) => state.poRequest.operationLoading.quoteItems);
  
  // Quote item history state
  const quoteItemHistory = useSelector(selectQuoteItemHistory);
  const isQuoteItemHistoryLoading = useSelector(selectIsQuoteItemHistoryLoading);
  const isQuoteItemDeleteLoading = useSelector((state: RootState) => state.quoteItem.operationLoading.delete);

  // Fetch PO Request Evaluation Ready List on component mount
  useEffect(() => {
    const userId = getCurrentUserId();
    console.log('Fetching PO Requests with decisionUserId:', userId);
    
    dispatch(getPORequests({ 
      RequestStatus: 'ReadyToEvaluate'
    }));
  }, [dispatch, getCurrentUserId]);


  // Handle error
  useEffect(() => {
    if (error) {
      showError(error);
    }
  }, [error, showError]);


  // Modal açıldığında seçimleri sıfırla
  useEffect(() => {
    if (showModal) {
      setSelectedSupplier('');
      setSelectedSuppliers([]);
      setIsMuzakere(false);
      setExpandedSuppliers(new Set());
    }
  }, [showModal]);


  // Transform PO Request data to offers format
  const offers: Offer[] = poRequests?.map((request: any) => ({
    id: request.id?.toString() || '',
    requestNo: request.id?.toString() || '',
    requestDate: request.createdAt ? formatDate(request.createdAt) : '',
    requestedDeliveryDate: request.requestedDeliveryDate ? formatDate(request.requestedDeliveryDate) : '',
    productCode: request.itemCode || '',
    productName: request.itemName || '',
    rootCode: request.rootCode || '',
    shortCode: request.shortCode || '',
    categoryName: request.categoryName || '',
    requestQuantity: request.approvedQuantity?.toString() || request.requestedQuantity?.toString() || '',
    unit: request.unit || '',
    inCart: false,
    cartQuantity: 0,
    cartPrice: 0,
    cartSupplier: '',
    decisionStatus: request.evaluationResult || '',
    relatedQuotes: [],
    itemId: request.itemId,
    decisionUserId: request.decisionUserId ?? null,
    batchId: (request as any).batchId || 0,
    createdByName: request.createdByName || '',
    note: (request as any).note ?? (request as any).Note ?? ''
  })) || [];





  const openEvaluationModal = useCallback(async (
    offer: Offer,
    quotesArray: any[],
    rateInfo: ExchangeRateInfo | null,
    rates: ManualRates
  ) => {
    const combinedOffer: Offer = {
      ...offer,
      relatedQuotes: quotesArray
    };

    // Satın alma geçmişini getir
    try {
      if (offer.itemId) {
        await dispatch(getItemPurchaseHistory({
          itemId: offer.itemId,
          pageIndex: 0,
          pageSize: 10
        }));
      }
    } catch (purchaseHistoryError) {
      // Satın alma geçmişi hatası modal açılmasını engellemez
      console.warn('Satın alma geçmişi yüklenemedi:', purchaseHistoryError);
    }

    // Modal açılırken seçimleri sıfırla
    setSelectedSupplier('');
    setSelectedSuppliers([]);
    setIsMuzakere(false);
    setManualRates(rates);
    setExchangeRateInfo(rateInfo);
    setSelectedOffer(combinedOffer);
    setShowModal(true);
  }, [dispatch]);

  const handleEvaluate = useCallback(async (offer: Offer, rates: ManualRates = {}) => {
    setIsLoadingQuoteItems(true);
    try {
      // Her seferinde fresh data al - cache kullanma
      const result = await dispatch(getPORequestQuoteItems({ poRequestId: parseInt(offer.id), ...rates }));

      if (getPORequestQuoteItems.fulfilled.match(result)) {
        const quoteItemsData = result.payload.response?.data;
        const quotesArray = Array.isArray(quoteItemsData?.items) ? quoteItemsData!.items : [];
        const rateInfo = quoteItemsData?.exchangeRates ?? null;

        // Netsis'te bugünün kuru yoksa bu açılışta kullanıcıdan kur iste
        if (Object.keys(rates).length === 0 && rateInfo && needsRatePrompt(rateInfo)) {
          setRatePrompt({ offer, quotes: quotesArray, info: rateInfo });
          return;
        }

        await openEvaluationModal(offer, quotesArray, rateInfo, rates);
      } else {
        showError('Teklif öğeleri yüklenirken hata oluştu.');
      }
    } catch (error) {
      showError('Teklif öğeleri yüklenirken hata oluştu.');
    } finally {
      setIsLoadingQuoteItems(false);
    }
  }, [dispatch, showError, openEvaluationModal]);

  const handleRatePromptSubmit = (rates: ManualRates) => {
    if (!ratePrompt) return;
    const { offer } = ratePrompt;
    setRatePrompt(null);
    handleEvaluate(offer, rates);
  };

  const handleRatePromptSkip = () => {
    if (!ratePrompt) return;
    const { offer, quotes, info } = ratePrompt;
    setRatePrompt(null);
    // Kur girilmeden devam: dolar karşılıkları boş kalır
    openEvaluationModal(offer, quotes, info, {});
  };

  const handleCloseModal = () => {
    if (isSavingSelection) {
      return;
    }
    setShowModal(false);
    setSelectedOffer(null);
    setSelectedSupplier('');
    setSelectedSuppliers([]);
    setIsMuzakere(false);
    setNegotiateValues({});
    setManualRates({});
    setExchangeRateInfo(null);
  };

  const handleRowDeleteClick = (offer: Offer) => {
    setOfferToDelete(offer);
    setShowDeleteConfirmation(true);
  };

  const confirmDelete = async () => {
    if (!offerToDelete) return;
    
    try {
      const id = parseInt(offerToDelete.id);
      await dispatch(deletePORequestWithQuoteItems(id)).unwrap();
      showSuccess('Talep ve ilgili teklif öğeleri başarıyla silindi.');
      
      setShowDeleteConfirmation(false);
      setOfferToDelete(null);
      
      // Refresh list
      dispatch(getPORequests({ 
        RequestStatus: 'ReadyToEvaluate'
      }));
      if (user?.id) {
        dispatch(getStatusCounts({ CreatedBy: user.id, ApproverUserId: user.id, DecisionUserId: user.id, PurchaserId: user.id }));
      }
    } catch (error) {
      showError('Talep silinirken bir hata oluştu.');
    }
  };

  const openQuoteItemDeleteConfirmation = (quoteItemId: number, supplierName: string) => {
    setQuoteItemToDelete({ id: quoteItemId, supplierName });
    setShowQuoteItemDeleteConfirmation(true);
  };

  const confirmQuoteItemDelete = async () => {
    if (!quoteItemToDelete || !selectedOffer) return;
    try {
      const deleteResult = await dispatch(deleteQuoteItem(quoteItemToDelete.id)).unwrap();
      showSuccess(deleteResult.message || 'Teklif kalemi silindi.');
      setShowQuoteItemDeleteConfirmation(false);
      setQuoteItemToDelete(null);
      // Aynı açılış: daha önce girilen kurla yenile, tekrar sorma
      const result = await dispatch(getPORequestQuoteItems({ poRequestId: parseInt(selectedOffer.id), ...manualRates }));
      if (getPORequestQuoteItems.fulfilled.match(result)) {
        const quoteItemsData = result.payload.response?.data;
        const quotesArray = Array.isArray(quoteItemsData?.items) ? quoteItemsData!.items : [];
        setExchangeRateInfo(quoteItemsData?.exchangeRates ?? null);
        setSelectedOffer((prev) => (prev ? { ...prev, relatedQuotes: quotesArray } : null));
      }
    } catch (error) {
      showError('Teklif kalemi silinirken bir hata oluştu.');
    }
  };

  const openItemView = useCallback((item: any) => {
    setItemToView(item);
    setShowItemViewModal(true);
  }, []);

  const closeItemViewModal = () => {
    setShowItemViewModal(false);
    setItemToView(null);
  };

  const openHistoryModal = async (quoteItemId: number, supplierName: string) => {
    setSelectedQuoteItemId(quoteItemId);
    setSelectedQuoteItemSupplierName(supplierName);
    setShowHistoryModal(true);
    
    // Fetch quote item history
    try {
      await dispatch(fetchQuoteItemHistory(quoteItemId));
    } catch (error) {
      console.error('Error fetching quote item history:', error);
    }
  };

  const closeHistoryModal = () => {
    setShowHistoryModal(false);
    setSelectedQuoteItemId(null);
    setSelectedQuoteItemSupplierName('');
    // History state'i temizlenmesi için clearCurrentQuote çağrılabilir ama sadece history'yi temizlemek için
    // Şimdilik state'i olduğu gibi bırakıyoruz, modal tekrar açıldığında yeni veri çekilecek
  };

  // Tedarikçi ismini kısaltan fonksiyon
  const truncateSupplierName = (supplierName: string, maxWords: number = 2) => {
    if (!supplierName) return 'Tedarikçi';
    const words = supplierName.split(' ');
    if (words.length <= maxWords) return supplierName;
    return words.slice(0, maxWords).join(' ') + '...';
  };

  // Ürün kodunu kısaltan fonksiyon
  const truncateProductCode = useCallback((productCode: string, maxLength: number = 12) => {
    if (!productCode) return '';
    if (productCode.length <= maxLength) return productCode;
    return productCode.substring(0, maxLength) + '...';
  }, []);

  // Tedarikçi ismini genişletme/daraltma fonksiyonu
  const toggleSupplierExpansion = (supplierId: string) => {
    setExpandedSuppliers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(supplierId)) {
        newSet.delete(supplierId);
      } else {
        newSet.add(supplierId);
      }
      return newSet;
    });
  };

  // Fiyat boş mu kontrolü
  const hasEmptyPrice = (quote: any): boolean => {
    if (!quote) return true;
    const price = quote.offeredPrice;
    return !price || price === '' || price === null || price === undefined || parseFloat(price) <= 0;
  };

  // Kaydet butonu aktiflik kontrolü
  const checkSaveDisabled = () => {
    if (isSavingSelection) return true;

    if (isMuzakere) {
      if (selectedSuppliers.length === 0) return true;
      
      // Hedef fiyat kontrolü: Fiyat < Hedef Fiyat olamaz
      return selectedSuppliers.some(supplierId => {
        const quote = selectedOffer?.relatedQuotes?.find(q => q.quoteItemsId?.toString() === supplierId);
        const negotiateData = negotiateValues[supplierId];
        
        if (!quote?.offeredPrice || !negotiateData?.targetPrice) return false;
        
        // Parse string price safely (replace , with .)
        const offeredPriceStr = quote.offeredPrice.toString().replace(',', '.');
        const offeredPrice = parseFloat(offeredPriceStr);
        
        // Fiyat < Hedef Fiyat ise hata (Offered Price < Target Price)
        return offeredPrice < negotiateData.targetPrice;
      });
    } else {
      return !selectedSupplier;
    }
  };

  const handleSelectSupplier = async () => {
    if (isPreviewMode || isSavingSelection) return;
    if (isMuzakere) {
      if (selectedSuppliers.length > 0) {
        // Müzakere modunda seçilen tedarikçilerin fiyatlarını kontrol et
        const suppliersWithEmptyPrice = selectedSuppliers.filter(supplierId => {
          const quote = selectedOffer?.relatedQuotes?.find(q => q.quoteItemsId?.toString() === supplierId);
          return hasEmptyPrice(quote);
        });

        if (suppliersWithEmptyPrice.length > 0) {
          const supplierNames = suppliersWithEmptyPrice.map(supplierId => {
            const quote = selectedOffer?.relatedQuotes?.find(q => q.quoteItemsId?.toString() === supplierId);
            return quote?.supplierName || 'Tedarikçi';
          }).join(', ');
          showWarning(`Fiyat bilgisi olmayan tedarikçiler seçilemez: ${supplierNames}`);
          return;
        }

        // Müzakere modunda tüm seçilen tedarikçiler için gerekli alanları kontrol et
        const missingFields = selectedSuppliers.some(supplierId => {
          const negotiateData = negotiateValues[supplierId];
          return !negotiateData?.targetQuantity || 
                 !negotiateData?.targetPrice || 
                 !negotiateData?.targetDeliveryDays ||
                 negotiateData.targetQuantity <= 0 ||
                 negotiateData.targetPrice <= 0 ||
                 negotiateData.targetDeliveryDays <= 0;
        });

        if (missingFields) {
          showWarning('Lütfen tüm seçilen tedarikçiler için geçerli miktar, fiyat ve teslim süresi giriniz.');
          return;
        }

        // Hedef fiyat kontrolü: Fiyat < Hedef Fiyat olamaz
        const invalidPriceSuppliers = selectedSuppliers.filter(supplierId => {
          const quote = selectedOffer?.relatedQuotes?.find(q => q.quoteItemsId?.toString() === supplierId);
          const negotiateData = negotiateValues[supplierId];
          
          if (!quote?.offeredPrice || !negotiateData?.targetPrice) return false;
          
          // Parse string price safely (replace , with .)
          const offeredPriceStr = quote.offeredPrice.toString().replace(',', '.');
          const offeredPrice = parseFloat(offeredPriceStr);
          
          // Fiyat < Hedef Fiyat ise hata (Offered Price < Target Price)
          return offeredPrice < negotiateData.targetPrice;
        });

        if (invalidPriceSuppliers.length > 0) {
          const supplierNames = invalidPriceSuppliers.map(supplierId => {
            const quote = selectedOffer?.relatedQuotes?.find(q => q.quoteItemsId?.toString() === supplierId);
            return quote?.supplierName || 'Tedarikçi';
          }).join(', ');
          
          showWarning(`Hedef fiyat, teklif edilen fiyattan büyük olamaz: ${supplierNames}`);
          return;
        }

        setIsSavingSelection(true);
        showLoading('Müzakere kayıt işlemleri tamamlanıyor...');
        try {
          // Müzakere modu: Seçilen tedarikçiler için negotiateQuoteItem sıralı çağır (deadlock önlemi)
          for (const supplierId of selectedSuppliers) {
            const quote = selectedOffer?.relatedQuotes?.find(q => q.quoteItemsId?.toString() === supplierId);
            const negotiateData = negotiateValues[supplierId];
            
            if (quote?.quoteItemsId && negotiateData) {
              await dispatch(negotiateQuoteItem({
                quoteItemId: quote.quoteItemsId,
                data: {
                  negotiationQuantity: negotiateData.targetQuantity!,
                  negotiationPrice: negotiateData.targetPrice!,
                  negotiationDeliveryDays: negotiateData.targetDeliveryDays!
                }
              })).unwrap();
            }
          }
          
          // Seçilmeyen tedarikçiler için setQuoteItemRejected sıralı çağır
          const allQuoteItemIds = (selectedOffer?.relatedQuotes && Array.isArray(selectedOffer.relatedQuotes) ? selectedOffer.relatedQuotes.map(quote => {
            return quote.quoteItemsId;
          }).filter(Boolean) : []);
          
          const rejectedQuoteItemIds = allQuoteItemIds.filter((id: any) => {
            return !selectedSuppliers.includes(id?.toString() || '');
          });
          
          for (const quoteItemId of rejectedQuoteItemIds) {
            await dispatch(setQuoteItemRejected({ id: quoteItemId! })).unwrap();
          }
          
          showSuccess('Kayıt başarılı');
          
          // Ana listeyi yenile
          await dispatch(getPORequests({ 
            RequestStatus: 'ReadyToEvaluate'
          })).unwrap();
          
          // Modal'ı kapat
          handleCloseModal();
        } catch (error) {
          showError('Tedarikçi seçimi sırasında bir hata oluştu.');
        } finally {
          hideLoading();
          setIsSavingSelection(false);
        }
      } else {
        showWarning('Lütfen en az bir tedarikçi seçiniz.');
      }
    } else {
      // Normal mod: Tek tedarikçi seçimi
      if (selectedSupplier) {
        // Seçilen tedarikçinin fiyatını kontrol et
        const selectedQuote = selectedOffer?.relatedQuotes?.find(q => q.quoteItemsId?.toString() === selectedSupplier);
        if (hasEmptyPrice(selectedQuote)) {
          const supplierName = selectedQuote?.supplierName || 'Tedarikçi';
          showWarning(`${supplierName} için fiyat bilgisi bulunmadığından teklif kaydedilemez.`);
          return;
        }

        setIsSavingSelection(true);
        showLoading('Tedarikçi seçimi kaydediliyor...');
        try {
          // Seçilen tedarikçinin quoteItem ID'sini al
          const selectedQuoteItemId = selectedQuote?.quoteItemsId;
          
          // Seçilmeyen tedarikçilerin quoteItem ID'lerini al
          const allQuoteItemIds = (selectedOffer?.relatedQuotes && Array.isArray(selectedOffer.relatedQuotes) ? selectedOffer.relatedQuotes.map(quote => {
            return quote.quoteItemsId;
          }).filter(Boolean) : []);

          // Seçilmeyen tedarikçileri reject et
          const rejectedQuoteItemIds = allQuoteItemIds.filter((id: any) => {
            return id !== selectedQuoteItemId;
          });

          if (selectedQuoteItemId) {
            // Karar kaydedilmesi ile siparişin oluşması ayrı iki olay: teklifte kararı
            // verilmemiş başka kalem varsa sipariş oluşmaz. Backend bunu checkedOut ile
            // bildiriyor; hangi adımda gerçekleştiği önemli olmadığı için OR'luyoruz.
            let orderCreated = false;

            // Seçilen tedarikçi için setQuoteItemAwarded çağır
            const awardResult = await dispatch(setQuoteItemAwarded({ id: selectedQuoteItemId })).unwrap();
            orderCreated = orderCreated || didCheckOut(awardResult);
            
            // Seçilmeyen tedarikçiler için setQuoteItemRejected sıralı çağır (deadlock önlemi)
            for (const quoteItemId of rejectedQuoteItemIds) {
              const rejectResult = await dispatch(setQuoteItemRejected({ id: quoteItemId! })).unwrap();
              orderCreated = orderCreated || didCheckOut(rejectResult);
            }
            
            const selectedQuoteName = selectedQuote?.supplierName || 'Tedarikçi';
            if (orderCreated) {
              showSuccess(`${selectedQuoteName} seçildi, satın alma siparişi oluşturuldu.`);
            } else {
              showWarning(`${selectedQuoteName} seçildi. Bu teklifteki diğer kalemler karara bağlanınca sipariş oluşacak.`);
            }
            
            // Ana listeyi yenile
            await dispatch(getPORequests({ 
              RequestStatus: 'ReadyToEvaluate'
            })).unwrap();
            
            // Modal'ı kapat
            handleCloseModal();
          }
        } catch (error) {
          showError('Tedarikçi seçimi sırasında bir hata oluştu.');
        } finally {
          hideLoading();
          setIsSavingSelection(false);
        }
      } else {
        showWarning('Lütfen bir tedarikçi seçiniz.');
      }
    }
  };

  const handleSupplierSelection = (supplierId: string) => {
    if (isPreviewMode || isSavingSelection) return;
    // Fiyat kontrolü - fiyatı olmayan tedarikçi seçilemez
    const quote = selectedOffer?.relatedQuotes?.find(q => q.quoteItemsId?.toString() === supplierId);
    if (hasEmptyPrice(quote)) {
      const supplierName = quote?.supplierName || 'Tedarikçi';
      showWarning(`${supplierName} için fiyat bilgisi bulunmadığından teklif seçilemez.`);
      return;
    }

    if (isMuzakere) {
      // Müzakere modu: Çoklu seçim
      setSelectedSuppliers(prev => {
        const isSelected = prev.includes(supplierId);
        if (isSelected) {
          // Remove from selection
          return prev.filter(id => id !== supplierId);
        } else {
          // Add to selection and set default values from quote
          if (quote && !negotiateValues[supplierId]?.targetQuantity && quote.offeredQuantity) {
            // Set default target quantity from offered quantity
            handleNegotiateValueChange(supplierId, 'targetQuantity', Number(quote.offeredQuantity));
          }
          if (quote && !negotiateValues[supplierId]?.targetPrice && quote.offeredPrice) {
            // Set default target price from offered price
            handleNegotiateValueChange(supplierId, 'targetPrice', parseFloat(quote.offeredPrice));
          }
          if (quote && !negotiateValues[supplierId]?.targetDeliveryDays && quote.offeredDeliveryDays) {
            // Set default target delivery days from offered delivery days
            handleNegotiateValueChange(supplierId, 'targetDeliveryDays', Number(quote.offeredDeliveryDays));
          }
          return [...prev, supplierId];
        }
      });
    } else {
      // Normal mod: Tek seçim (checkbox gibi davranır ama tek seçim yapar)
      if (selectedSupplier === supplierId) {
        // Aynı tedarikçiye tekrar tıklandıysa seçimi kaldır
        setSelectedSupplier('');
      } else {
        // Farklı tedarikçi seçildiyse onu seç
        setSelectedSupplier(supplierId);
      }
    }
  };

  // Müzakere input değerlerini güncelle
  const handleNegotiateValueChange = (supplierId: string, field: 'targetQuantity' | 'targetPrice' | 'targetDeliveryDays', value: number) => {
    setNegotiateValues(prev => ({
      ...prev,
      [supplierId]: {
        ...prev[supplierId],
        [field]: value
      }
    }));
  };




  // Use API data
  const displayOffers = offers;

  const filteredOffers = displayOffers.filter(offer => {
    // Tek arama terimi ile tüm alanlarda arama yap
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const matchesRequestNo = (offer.requestNo?.toLowerCase() || '').includes(term);
      const matchesProductCode = (offer.productCode?.toLowerCase() || '').includes(term);
      const matchesShortCode = (offer.shortCode?.toLowerCase() || '').includes(term);
      const matchesProductName = (offer.productName?.toLowerCase() || '').includes(term);
      
      if (!matchesRequestNo && !matchesProductCode && !matchesShortCode && !matchesProductName) {
        return false;
      }
    }
    const matchesCategory = selectedCategory === '' || (offer.categoryName?.toLowerCase() || '').includes(selectedCategory.toLowerCase());
    if (!matchesCategory) return false;
    // NOT: Önceden burada "değerlendirmesi yapılmış" (evaluationResult dolu) kalemler
    // listeden çıkarılıyordu. Ancak backend bu ekrana yalnızca RequestStatus =
    // 'ReadyToEvaluate' olan talepleri döndürüyor; değerlendirilmiş görünen ama hâlâ
    // değerlendirmeye hazır durumda olan bir kalem, kararı kaydedilmiş fakat siparişi
    // oluşmamış bir anomalidir. Onu gizlemek kullanıcının düzeltmesini imkânsız kılıyordu,
    // bu yüzden gösteriyoruz; "Önceki Seçim" kolonu durumu ayırt etmeyi sağlıyor.
    return true;
  });

  const sortedOffers = [...filteredOffers].sort((a, b) => {
    // Talep no'ya göre sıralama (desc - büyükten küçüğe)
    const idA = parseInt(a.requestNo) || 0;
    const idB = parseInt(b.requestNo) || 0;
    return idB - idA;
  });

  // Group offers by batchId
  const groupedByBatch = sortedOffers.reduce((acc, offer) => {
    const batchId = (offer as any).batchId || 0;
    if (!acc[batchId]) {
      acc[batchId] = [];
    }
    acc[batchId]!.push(offer);
    return acc;
  }, {} as Record<number, typeof sortedOffers>);

  // Toggle batch expansion
  const toggleBatch = (batchId: number) => {
    setExpandedBatches(prev => {
      const newSet = new Set(prev);
      if (newSet.has(batchId)) {
        newSet.delete(batchId);
      } else {
        newSet.add(batchId);
      }
      return newSet;
    });
  };

  // Toggle all batches
  const allBatchIds = Object.keys(groupedByBatch).map(id => parseInt(id));
  const isAllExpanded = allBatchIds.length > 0 && allBatchIds.every(id => expandedBatches.has(id));

  const toggleAllBatches = () => {
    if (isAllExpanded) {
      setExpandedBatches(new Set());
    } else {
      setExpandedBatches(new Set(allBatchIds));
    }
  };

  const parseDateStr = (s?: string) => {
    if (!s) return 0;
    const parts = s.split('.');
    if (parts.length === 3) return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`).getTime() || 0;
    return new Date(s).getTime() || 0;
  };

  const childColumnDefs = useMemo<ColDef<Offer>[]>(() => [
    {
      headerName: 'Talep No',
      field: 'requestNo',
      minWidth: 70,
      flex: 0.6,
      comparator: (a: string, b: string) => (parseInt(a) || 0) - (parseInt(b) || 0),
    },
    {
      headerName: 'Oluşturan',
      colId: 'createdByWithDate',
      minWidth: 115,
      flex: 0.95,
      sortable: true,
      comparator: (_a, _b, nodeA, nodeB) => {
        const da = parseDateStr((nodeA?.data as Offer | undefined)?.requestDate || '');
        const db = parseDateStr((nodeB?.data as Offer | undefined)?.requestDate || '');
        return da - db;
      },
      valueGetter: (params) => {
        const o = params.data as Offer | undefined;
        const name = o?.createdByName?.trim() || '-';
        const date = o?.requestDate?.trim() || '-';
        return `${name}\n${date}`;
      },
      wrapText: true,
      autoHeight: true,
      cellRenderer: (params: ICellRendererParams<Offer>) => {
        const o = params.data;
        if (!o) return null;
        const name = o.createdByName?.trim() || '-';
        const date = o.requestDate?.trim() || '-';
        return (
          <div className="flex flex-col gap-0.5 py-0.5 leading-tight text-left">
            <span className="text-xs font-medium text-gray-800 dark:text-gray-100">{name}</span>
            <span className="text-[10px] text-gray-500 dark:text-gray-400">{date}</span>
          </div>
        );
      },
    },
    {
      headerName: 'İst. Talep Tarihi',
      field: 'requestedDeliveryDate',
      minWidth: 100,
      flex: 0.9,
      comparator: (a: string, b: string) => parseDateStr(a) - parseDateStr(b),
    },
    {
      headerName: 'Kısa Kod',
      field: 'shortCode',
      minWidth: 70,
      flex: 0.6,
    },
    {
      headerName: 'Ürün',
      colId: 'productSummary',
      minWidth: 200,
      flex: 2.2,
      sortable: true,
      comparator: (_a, _b, nodeA, nodeB) => {
        const na = ((nodeA?.data as Offer)?.productName || '').toLocaleLowerCase('tr-TR');
        const nb = ((nodeB?.data as Offer)?.productName || '').toLocaleLowerCase('tr-TR');
        return na.localeCompare(nb, 'tr');
      },
      valueGetter: (params) => {
        const o = params.data as Offer | undefined;
        if (!o) return '';
        return [o.productCode, o.productName, o.categoryName].filter(Boolean).join(' | ');
      },
      tooltipValueGetter: (params) => {
        const o = params.data as Offer | undefined;
        if (!o) return '';
        const parts = [o.productCode, o.productName, o.categoryName].filter((x) => (x ?? '').toString().trim());
        return parts.join('\n');
      },
      autoHeight: true,
      cellRenderer: (params: ICellRendererParams<Offer>) => {
        const offer = params.data;
        if (!offer) return null;
        const code = offer.productCode?.trim();
        const pname = offer.productName?.trim() || '-';
        const cat = offer.categoryName?.trim() || '-';
        return (
          <div className="flex flex-col gap-0.5 py-0.5 leading-tight text-left">
            {code ? (
              <span
                className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 cursor-pointer underline font-medium"
                onClick={(e) => {
                  e.stopPropagation();
                  openItemView(offer);
                }}
                title={code.startsWith('PRJ') ? 'Proje detayları' : 'Ürün detayları'}
              >
                {truncateProductCode(code)}
                {offer.shortCode ? ` · ${offer.shortCode}` : ''}
              </span>
            ) : (
              <span className="text-xs text-gray-500 dark:text-gray-400">-</span>
            )}
            <span className="text-xs font-medium text-gray-800 dark:text-gray-100">{pname}</span>
            <span className="text-[10px] text-gray-500 dark:text-gray-400">{cat}</span>
          </div>
        );
      },
    },
    {
      headerName: 'Talep Mikt.',
      field: 'requestQuantity',
      minWidth: 90,
      flex: 0.8,
      comparator: (a: string, b: string) => (parseFloat(a) || 0) - (parseFloat(b) || 0),
      valueFormatter: (params) => {
        const offer = params.data as Offer | undefined;
        if (!offer?.requestQuantity) return '-';
        return `${Number(offer.requestQuantity).toLocaleString('tr-TR')} ${offer.unit || ''}`.trim();
      },
    },
    {
      // Bu kolon yalnızca anomali durumunda dolu olur: kalem daha önce bir tedarikçiye
      // verilmiş ama siparişi oluşmamışsa, hangi tedarikçinin seçildiğini gösterir.
      headerName: 'Önceki Seçim',
      colId: 'previousDecision',
      minWidth: 120,
      flex: 0.9,
      valueGetter: (params) => (params.data as Offer | undefined)?.decisionStatus || '',
      tooltipValueGetter: (params) => {
        const value = (params.data as Offer | undefined)?.decisionStatus;
        return value ? `${value} seçilmiş ancak sipariş oluşmamış — yeniden değerlendirilebilir` : '';
      },
      cellRenderer: (params: ICellRendererParams<Offer>) => {
        const value = params.data?.decisionStatus?.toString().trim();
        if (!value) return '';
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
            <i className="fas fa-exclamation-triangle" />
            {value}
          </span>
        );
      },
    },
    {
      headerName: 'Not',
      minWidth: 130,
      flex: 1,
      valueGetter: (params) => {
        const offer = params.data as Offer | undefined;
        return (offer as any)?.note ?? (offer as any)?.Note ?? '';
      },
      tooltipValueGetter: (params) => {
        const offer = params.data as Offer | undefined;
        return (offer as any)?.note ?? (offer as any)?.Note ?? '';
      },
    },
    {
      headerName: 'İşlem',
      sortable: false,
      filter: false,
      resizable: false,
      minWidth: 100,
      flex: 0.8,
      cellRenderer: (params: ICellRendererParams<Offer>) => {
        const offer = params.data;
        if (!offer) return null;
        const rowPreview = Boolean(
          offer.decisionUserId != null &&
          getCurrentUserId() !== undefined &&
          offer.decisionUserId !== getCurrentUserId()
        );
        return (
          <div className="flex items-center justify-center gap-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleEvaluate(offer);
              }}
              disabled={isLoadingQuoteItems}
              className={`px-1 py-px rounded text-[10px] leading-tight transition-colors ${
                isLoadingQuoteItems
                  ? 'bg-gray-400 text-white cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {isLoadingQuoteItems ? 'Yükleniyor...' : 'Değerlendir'}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (!rowPreview) handleRowDeleteClick(offer);
              }}
              disabled={rowPreview}
              className={`p-1 transition-colors ${
                rowPreview
                  ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
                  : 'text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300'
              }`}
              title={rowPreview ? 'Bu talep size atanmadığı için silme işlemi yapamazsınız' : 'Sil'}
            >
              <i className="fas fa-trash-alt"></i>
            </button>
          </div>
        );
      },
    },
  ], [isLoadingQuoteItems, getCurrentUserId, handleEvaluate, openItemView, truncateProductCode]);

  const childDefaultColDef = useMemo<ColDef>(() => ({
    flex: 1,
  }), []);

  return (
    <Layout  currentScreen="quote-evaluation">
      <div className="max-w-8xl mx-auto h-full flex flex-col">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-2 transition-colors duration-300 flex-shrink-0">
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-end gap-3">
            {/* Search bar - dinamik genişlik */}
            <div className="flex-1 min-w-[300px]">
              <input 
                type="text" 
                placeholder="Talep No, Ürün Kodu, Kısa Kod veya Ürün Adı ile Ara..." 
                value={searchTerm} 
                onChange={(e) => setSearchTerm(e.target.value)} 
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 text-sm" 
              />
            </div>
            {/* Filtreler ve Yenile */}
            <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
              <select 
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              >
                <option value="">Tüm Kategoriler</option>
                {Array.from(
                  new Set(
                    offers
                      .map(offer => offer.categoryName)
                      .filter(Boolean)
                  )
                )
                  .sort((a, b) => (a || '').localeCompare(b || '', 'tr'))
                  .map(categoryName => (
                    <option key={categoryName} value={categoryName}>{categoryName}</option>
                  ))
                }
              </select>
              <select 
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              >
                <option value="delivery-date-asc">İst. Talep Tarihi (Eski-Yeni)</option>
                <option value="delivery-date-desc">İst. Talep Tarihi (Yeni-Eski)</option>
                <option value="product">Ürün A-Z</option>
              </select>
              <button 
                onClick={() => {
                  dispatch(getPORequests({ 
                    RequestStatus: 'ReadyToEvaluate'
                  }));
                }}
                className="px-3 py-2 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                <i className="fas fa-sync-alt mr-2"></i>Yenile
              </button>
            </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-auto transition-colors duration-300 max-h-[calc(100vh-300px)]">
          {/* Expandable Grid */}
          {isLoading ? (
            <div className="px-6 py-12 text-center">
              <div className="flex items-center justify-center space-x-3">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                <span className="text-gray-600 dark:text-gray-400">Veriler yükleniyor...</span>
              </div>
            </div>
          ) : Object.keys(groupedByBatch).length === 0 ? (
            <div className="px-6 py-12 text-center">
              <div className="text-gray-500 dark:text-gray-400">
                <i className="fas fa-inbox text-4xl mb-4"></i>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  {searchTerm || selectedCategory
                    ? 'Arama kriterlerinize uygun veri bulunamadı'
                    : 'Değerlendirilmemiş talep bulunmuyor'}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {searchTerm || selectedCategory
                    ? 'Farklı arama kriterleri deneyebilir veya filtreleri temizleyebilirsiniz.'
                    : 'Tüm kalemler değerlendirilmiş olabilir veya henüz bu ekranda gösterilecek talep bulunmuyor.'}
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Mobile Card Layout */}
              <div className="lg:hidden overflow-auto flex-1">
                <div className="p-2 flex items-center justify-between border-b border-gray-200 dark:border-gray-700">
                  <span className="text-xs text-gray-500 dark:text-gray-400">{Object.keys(groupedByBatch).length} Parti</span>
                  <button
                    onClick={toggleAllBatches}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                  >
                    {isAllExpanded ? 'Hepsini Daralt' : 'Hepsini Genişlet'}
                  </button>
                </div>
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {Object.entries(groupedByBatch).map(([batchIdStr, batchOffers]) => {
                    const batchId = parseInt(batchIdStr);
                    const isExpanded = expandedBatches.has(batchId);
                    const firstOffer = batchOffers[0];
                    const uniqueCategories = Array.from(new Set(batchOffers.map(o => (o as any).categoryName).filter(Boolean))).join(', ');

                    if (!firstOffer) return null;

                    return (
                      <div key={batchId}>
                        <div
                          className="px-4 py-3 flex items-center gap-3 bg-gray-50 dark:bg-gray-700/50 cursor-pointer active:bg-gray-100 dark:active:bg-gray-700 transition-colors"
                          onClick={() => toggleBatch(batchId)}
                        >
                          <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'} text-xs text-gray-500 dark:text-gray-400 w-4`}></i>
                          <div className="flex-1 min-w-0 flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
                            <span className="text-sm font-semibold text-gray-900 dark:text-white shrink-0">Parti #{batchId || '-'}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0">—</span>
                            <span className="text-xs font-medium text-blue-600 dark:text-blue-400 min-w-0 break-words">{uniqueCategories || '-'}</span>
                          </div>
                        </div>
                        {isExpanded && (
                          <div className="bg-white dark:bg-gray-800">
                            {batchOffers.map((offer) => {
                              const rowPreview = Boolean(
                                offer.decisionUserId != null &&
                                getCurrentUserId() !== undefined &&
                                offer.decisionUserId !== getCurrentUserId()
                              );
                              return (
                                <div key={offer.id} className="px-4 py-3 border-t border-gray-100 dark:border-gray-700/50">
                                  <div className="flex items-start justify-between gap-2 mb-2">
                                    <div className="min-w-0 flex-1">
                                      <p className="text-[10px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Ürün</p>
                                      <div className="flex flex-col gap-0.5">
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                          <span
                                            className="text-blue-600 dark:text-blue-400 underline cursor-pointer font-medium"
                                            onClick={(e) => { e.stopPropagation(); openItemView(offer); }}
                                          >
                                            {offer.productCode ? truncateProductCode(offer.productCode) : '-'}
                                          </span>
                                          {offer.shortCode ? ` · ${offer.shortCode}` : ''}
                                        </p>
                                        <p className="text-sm font-medium text-gray-900 dark:text-white">{offer.productName || '-'}</p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">{offer.categoryName || '-'}</p>
                                      </div>
                                    </div>
                                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400 flex-shrink-0">#{offer.requestNo}</span>
                                  </div>
                                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                                    <div className="flex justify-between">
                                      <span className="text-gray-500 dark:text-gray-400">Talep Mikt.:</span>
                                      <span className="text-gray-700 dark:text-gray-300">{offer.requestQuantity ? `${Number(offer.requestQuantity).toLocaleString('tr-TR')} ${offer.unit || ''}`.trim() : '-'}</span>
                                    </div>
                                    <div className="flex justify-between items-start col-span-2 gap-2">
                                      <span className="text-gray-500 dark:text-gray-400 flex-shrink-0">Oluşturan:</span>
                                      <div className="text-right text-gray-700 dark:text-gray-300 min-w-0">
                                        <div className="font-medium">{offer.createdByName?.trim() || '-'}</div>
                                        <div className="text-gray-500 dark:text-gray-400">{offer.requestDate || '-'}</div>
                                      </div>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-500 dark:text-gray-400">Teslim T.:</span>
                                      <span className="text-gray-700 dark:text-gray-300">{offer.requestedDeliveryDate || '-'}</span>
                                    </div>
                                    {((offer as any)?.note || (offer as any)?.Note) && (
                                      <div className="flex justify-between col-span-2">
                                        <span className="text-gray-500 dark:text-gray-400">Not:</span>
                                        <span className="text-gray-700 dark:text-gray-300 truncate ml-2">{(offer as any)?.note ?? (offer as any)?.Note}</span>
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2 mt-2">
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleEvaluate(offer); }}
                                      disabled={isLoadingQuoteItems}
                                      className={`px-2 py-1 rounded text-xs transition-colors ${
                                        isLoadingQuoteItems ? 'bg-gray-400 text-white cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'
                                      }`}
                                    >
                                      {isLoadingQuoteItems ? 'Yükleniyor...' : 'Değerlendir'}
                                    </button>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); if (!rowPreview) handleRowDeleteClick(offer); }}
                                      disabled={rowPreview}
                                      className={`p-1 transition-colors ${
                                        rowPreview ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed' : 'text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300'
                                      }`}
                                      title={rowPreview ? 'Bu talep size atanmadığı için silme işlemi yapamazsınız' : 'Sil'}
                                    >
                                      <i className="fas fa-trash-alt text-xs"></i>
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Desktop Table + Grid Layout */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full border border-gray-300 dark:border-gray-600" style={{ tableLayout: 'fixed' }}>
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="border border-gray-300 dark:border-gray-600 px-1.5 py-1 text-center text-[11px] font-medium text-gray-700 dark:text-gray-300" style={{ width: '36px' }}>
                        <button 
                          onClick={toggleAllBatches}
                          className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors focus:outline-none"
                          title={isAllExpanded ? "Hepsini Daralt" : "Hepsini Genişlet"}
                        >
                          <i className={`fas fa-angle-double-${isAllExpanded ? 'up' : 'down'} text-xs`}></i>
                        </button>
                      </th>
                      <th className="border border-gray-300 dark:border-gray-600 px-1.5 py-1 text-left text-[11px] font-medium text-gray-700 dark:text-gray-300">Parti</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(groupedByBatch).map(([batchIdStr, batchOffers]) => {
                      const batchId = parseInt(batchIdStr);
                      const isExpanded = expandedBatches.has(batchId);
                      const firstOffer = batchOffers[0];
                      const uniqueCategories = Array.from(new Set(batchOffers.map(o => (o as any).categoryName).filter(Boolean))).join(', ');
                      
                      if (!firstOffer) return null;
                      
                      return (
                        <React.Fragment key={batchId}>
                          {/* Parent Row */}
                          <tr
                            className="bg-white dark:bg-gray-800 hover:bg-blue-50 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                            onClick={() => toggleBatch(batchId)}
                          >
                            <td className="border border-gray-300 dark:border-gray-600 px-1.5 py-1 text-center align-middle">
                              <i 
                                className={`fas fa-chevron-${isExpanded ? 'down' : 'right'} text-[10px] text-gray-500 dark:text-gray-400`}
                              ></i>
                            </td>
                            <td className="border border-gray-300 dark:border-gray-600 px-1.5 py-1 text-xs text-gray-800 dark:text-gray-100">
                              <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-2 leading-tight gap-0.5">
                                <span className="font-semibold text-gray-800 dark:text-gray-100 whitespace-nowrap shrink-0">
                                  Parti #{batchId || '-'}
                                </span>
                                <span className="text-gray-500 dark:text-gray-400 hidden sm:inline shrink-0">—</span>
                                <span className="font-medium text-gray-700 dark:text-gray-200 min-w-0 break-words">
                                  {uniqueCategories || '-'}
                                </span>
                              </div>
                            </td>
                          </tr>
                          
                          {/* Child Grid - Expanded */}
                          {isExpanded && (
                            <tr>
                              <td colSpan={2} className="border border-gray-300 dark:border-gray-600 p-0">
                                <div className="ag-child-grid">
                                  <BaseGrid<Offer>
                                    ref={gridRef}
                                    gridStateKey="ag-grid-quote-evaluation-child"
                                    rowData={batchOffers}
                                    columnDefs={childColumnDefs}
                                    defaultColDef={childDefaultColDef}
                                    getRowId={(params) => params.data.id}
                                    suppressNoRowsOverlay={true}
                                    autoSizeStrategy={{ type: 'fitGridWidth' }}
                                  />
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* Teklif Kıyaslama Modal */}
        {showModal && selectedOffer && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-1 sm:p-4 overflow-y-auto">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-7xl mx-auto my-2 sm:my-4 max-h-[calc(100vh-1rem)] sm:max-h-[90vh] overflow-y-auto transition-colors duration-300">
              {/* Modal Header */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-2 sm:p-3 border-b border-gray-200 dark:border-gray-700 transition-colors duration-300 relative">
                {/* Close Button - Fixed Position */}
                  <button 
                    onClick={handleCloseModal}
                    disabled={isSavingSelection}
                  className="absolute top-2 right-2 sm:top-3 sm:right-3 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 text-lg sm:text-xl transition-colors z-10 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                
                {/* Header Content */}
                <div className="pr-6 sm:pr-8">
                  {isPreviewMode && (
                    <div className="mb-2 px-3 py-2 text-gray-900 dark:text-white text-sm flex items-center gap-2">
                      <i className="fas fa-eye"></i>
                      <span>Bu talep size atanmadığı için değişiklik yapamazsınız.</span>
                    </div>
                  )}
                  <div className="flex items-start sm:items-center">
                    <div className="bg-blue-100 dark:bg-blue-900/30 rounded-lg p-1.5 sm:p-2 mr-2 sm:mr-3 flex-shrink-0">
                      <i className="fas fa-chart-line text-blue-600 text-base sm:text-lg"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="text-base sm:text-xl font-bold text-gray-900 dark:text-white transition-colors mb-0.5">Teklif Kıyaslama</h2>
                      <p className="text-xs text-gray-600 dark:text-gray-400 transition-colors mb-1 break-words">
                        Talep ID: {selectedOffer.requestNo} - {selectedOffer.productName}
                      </p>
                      <div className="text-xs text-gray-600 dark:text-gray-400 transition-colors space-y-0.5 sm:space-y-0 sm:flex sm:flex-wrap">
                        <span className="block sm:inline sm:mr-3"><strong>Kısa Kod:</strong> {selectedOffer.shortCode || '-'}</span>
                        <span className="block sm:inline sm:mr-3"><strong>Kategori:</strong> {selectedOffer.categoryName || 'Ofis Malzemeleri'}</span>
                        <span className="block sm:inline sm:mr-3"><strong>Talep Miktarı:</strong> {selectedOffer.requestQuantity ? `${Number(selectedOffer.requestQuantity).toLocaleString('tr-TR')} ${selectedOffer.unit || ''}`.trim() : '-'}</span>
                        <span className="block sm:inline"><strong>İst. Talep Tarihi:</strong> {selectedOffer.requestedDeliveryDate || '-'}</span>
                      </div>
                      <ManualRateNote info={exchangeRateInfo} className="mt-2 inline-block" />
                    </div>
                  </div>
                </div>
              </div>

                             {/* Modal Content */}
               <div className="p-2 sm:p-3">
                 {/* Teklif Karşılaştırma Tablosu */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden mb-3 sm:mb-4 transition-colors duration-300">
                  <div className="p-2 sm:p-3 border-b border-gray-200 dark:border-gray-700 flex flex-col gap-2 sm:gap-3 transition-colors duration-300">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-end gap-2 sm:gap-3">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="muzakere-toggle"
                          checked={isMuzakere}
                          onChange={() => {
                            if (isPreviewMode || isSavingSelection) return;
                            setIsMuzakere(!isMuzakere);
                            setSelectedSupplier('');
                            setSelectedSuppliers([]);
                          }}
                          disabled={isPreviewMode || isSavingSelection}
                          className="w-4 h-4 text-blue-600 focus:ring-blue-500 bg-gray-50 dark:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                        <label htmlFor="muzakere-toggle" className={`ml-2 text-sm text-gray-700 dark:text-gray-300 ${isPreviewMode ? 'opacity-60 cursor-not-allowed' : ''}`}>
                          Müzakere Modu
                        </label>
                      </div>
                      <button 
                        onClick={handleSelectSupplier}
                        disabled={checkSaveDisabled() || isPreviewMode || isSavingSelection}
                        className={`flex items-center px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg font-medium transition-colors text-xs sm:text-sm ${
                          !checkSaveDisabled() && !isPreviewMode && !isSavingSelection
                            ? 'bg-green-600 text-white hover:bg-green-700' 
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        <i className="fas fa-save mr-1.5"></i>
                        {isSavingSelection ? 'Kaydediliyor...' : 'Kaydet'}
                      </button>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    {isQuoteItemsLoading ? (
                      <div className="flex items-center justify-center py-6 sm:py-8">
                        <div className="text-center">
                          <div className="animate-spin rounded-full h-5 w-5 sm:h-6 sm:w-6 border-b-2 border-blue-600 mx-auto mb-3"></div>
                          <p className="text-gray-500 dark:text-gray-400 text-xs sm:text-sm">Teklifler yükleniyor...</p>
                        </div>
                      </div>
                    ) : (
                      <>
                        {/* Desktop Table */}
                        <div className="hidden lg:block">
                      <table className="w-full min-w-[800px]">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                          <tr>
                            <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider transition-colors whitespace-nowrap">Kriterler</th>
                            {selectedOffer?.relatedQuotes && Array.isArray(selectedOffer.relatedQuotes) && selectedOffer.relatedQuotes.length > 0 ? selectedOffer.relatedQuotes.map((quote) => {
                              const supplierId = quote.quoteItemsId?.toString() || '';
                              const isExpanded = expandedSuppliers.has(supplierId);
                              const displayName = isExpanded 
                                ? (quote.supplierName || 'Tedarikçi')
                                : truncateSupplierName(quote.supplierName || 'Tedarikçi');
                              
                              return (
                                <th key={quote.quoteItemsId} className="px-3 sm:px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider transition-colors whitespace-nowrap">
                                  <div className="flex flex-col items-center">
                                    <div className="w-6 h-6 rounded-full flex items-center justify-center mb-1 bg-blue-100 dark:bg-blue-900/30">
                                      <i className="fas fa-building text-xs text-blue-600"></i>
                                    </div>
                                    <span 
                                      className="dark:text-white text-xs transition-colors cursor-pointer hover:text-blue-600 dark:hover:text-blue-400"
                                      onClick={() => toggleSupplierExpansion(supplierId)}
                                      title={quote.supplierName || 'Tedarikçi'}
                                    >
                                      {displayName}
                                    </span>
                                    <span className="mt-1 flex items-center justify-center gap-1.5 flex-wrap">
                                      {quote.round != null && (
                                        <span className="text-[10px] text-gray-500 dark:text-gray-400 font-bold">Tur {quote.round}</span>
                                      )}
                                      {quote.isNegotiate && (
                                        <span className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-[10px] font-bold rounded border border-gray-200 dark:border-gray-600 uppercase tracking-tighter">
                                          Müzakere
                                        </span>
                                      )}
                                      {quote.quoteStatus === 'Closed' && (
                                        <span
                                          className="px-1.5 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-[10px] font-bold rounded border border-red-200 dark:border-red-800 uppercase tracking-tighter"
                                          title="Bu teklif için tedarikçinin yanıt süresi dolduğundan teklif sistem tarafından otomatik kapatılmıştır."
                                        >
                                          Süresi Geçmiş
                                        </span>
                                      )}
                                    </span>
                                    <span className="mt-1 flex items-center justify-center gap-1">
                                      <button
                                        onClick={() => openHistoryModal(quote.quoteItemsId || 0, quote.supplierName || 'Tedarikçi')}
                                        className="text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                                        title="Geçmiş"
                                      >
                                        <i className="fas fa-history text-xs"></i>
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (quote.quoteItemsId && !isPreviewMode) {
                                            openQuoteItemDeleteConfirmation(quote.quoteItemsId, quote.supplierName || 'Tedarikçi');
                                          }
                                        }}
                                        className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 transition-colors"
                                        title="Teklif kalemini sil"
                                        disabled={isPreviewMode}
                                      >
                                        <i className="fas fa-trash-alt text-xs"></i>
                                      </button>
                                    </span>
                                  </div>
                                </th>
                              );
                            }) : (
                              <th className="px-3 sm:px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider transition-colors whitespace-nowrap">
                                <div className="flex flex-col items-center">
                                  <div className="w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-600 flex items-center justify-center mb-1">
                                    <i className="fas fa-exclamation-triangle text-gray-500 text-xs"></i>
                                  </div>
                                  <span className="dark:text-white text-xs">Teklif Bulunamadı</span>
                                </div>
                              </th>
                            )}
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        <tr>
                          <td className="px-3 sm:px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white transition-colors">Seçim</td>
                          {selectedOffer?.relatedQuotes && Array.isArray(selectedOffer.relatedQuotes) && selectedOffer.relatedQuotes.length > 0 ? selectedOffer.relatedQuotes.map((quote) => {
                            const quoteId = quote.quoteItemsId?.toString() || '';
                            const isChecked = isMuzakere ? selectedSuppliers.includes(quoteId) : selectedSupplier === quoteId;
                            const hasPrice = !hasEmptyPrice(quote);
                            return (
                              <td key={quote.quoteItemsId} className="px-3 sm:px-4 py-2 text-center">
                                <input 
                                  type="checkbox" 
                                  checked={isChecked}
                                  onChange={() => !isPreviewMode && handleSupplierSelection(quoteId)}
                                  disabled={!hasPrice || isPreviewMode}
                                  title={isPreviewMode ? 'Önizleme modu' : !hasPrice ? 'Fiyat bilgisi olmayan teklif seçilemez' : ''}
                                  className={`w-4 h-4 text-blue-600 focus:ring-blue-500 bg-gray-50 dark:bg-gray-700 ${
                                    hasPrice && !isPreviewMode ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
                                  }`}
                                />
                              </td>
                            );
                          }) : (
                            <td className="px-3 sm:px-4 py-2 text-center text-sm text-gray-500 dark:text-gray-400">
                              -
                            </td>
                          )}
                        </tr>
                        <tr>
                          <td className="px-3 sm:px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white transition-colors">Miktar</td>
                          {selectedOffer?.relatedQuotes && Array.isArray(selectedOffer.relatedQuotes) && selectedOffer.relatedQuotes.length > 0 ? selectedOffer.relatedQuotes.map((quote) => {
                            return (
                              <td key={quote.quoteItemsId} className="px-3 sm:px-4 py-2 text-center text-sm text-gray-900 dark:text-white transition-colors">
                                {quote.offeredQuantity ? Number(quote.offeredQuantity).toLocaleString('tr-TR') : '-'}
                              </td>
                            );
                          }) : (
                            <td className="px-3 sm:px-4 py-2 text-center text-sm text-gray-500 dark:text-gray-400">
                              -
                            </td>
                          )}
                        </tr>
                        <tr>
                          <td className="px-3 sm:px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white transition-colors">Fiyat</td>
                          {selectedOffer?.relatedQuotes && Array.isArray(selectedOffer.relatedQuotes) && selectedOffer.relatedQuotes.length > 0 ? selectedOffer.relatedQuotes.map((quote) => {
                            return (
                              <td key={quote.quoteItemsId} className="px-3 sm:px-4 py-2 text-center text-gray-900 dark:text-white transition-colors">
                                {quote.offeredPrice ? (
                                  <div>
                                    <div className="flex items-center justify-center gap-1">
                                      <NumericFormat
                                        value={parseFloat(quote.offeredPrice)}
                                        displayType="text"
                                        thousandSeparator="."
                                        decimalSeparator=","
                                        decimalScale={4}
                                        fixedDecimalScale={true}
                                        suffix={quote.currency ? ` ${quote.currency}` : ''}
                                        className="text-sm font-medium text-gray-900 dark:text-white"
                                      />
                                      {quote.vatIncluded ? (
                                        <div className="text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
                                          (KDV dahil {quote.vatRate || 0}%)
                                        </div>
                                      ) : (
                                        <div className="text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
                                          (+KDV {quote.vatRate || 0}%)
                                        </div>
                                      )}
                                    </div>
                                    {quote.offeredPriceUSD != null && (
                                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                        <PriceInput
                                          value={parseFloat(quote.offeredPriceUSD)}
                                          onChange={() => {}}
                                          currency="USD"
                                          disabled={true}
                                          decimalScale={4}
                                          fixedDecimalScale={true}
                                          className="text-xs bg-transparent border-none p-0 cursor-default"
                                        />
                                      </div>
                                    )}
                                  </div>
                                ) : '-'}
                              </td>
                            );
                          }) : (
                            <td className="px-3 sm:px-4 py-2 text-center text-sm text-gray-500 dark:text-gray-400">
                              -
                            </td>
                          )}
                        </tr>
                        <tr>
                          <td className="px-3 sm:px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white transition-colors">Teslim Süresi</td>
                          {selectedOffer?.relatedQuotes && Array.isArray(selectedOffer.relatedQuotes) && selectedOffer.relatedQuotes.length > 0 ? selectedOffer.relatedQuotes.map((quote) => {
                            return (
                              <td key={quote.quoteItemsId} className="px-3 sm:px-4 py-2 text-center text-sm text-gray-900 dark:text-white transition-colors">
                                {quote.offeredDeliveryDays ? `${quote.offeredDeliveryDays} gün` : '-'}
                              </td>
                            );
                          }) : (
                            <td className="px-3 sm:px-4 py-2 text-center text-sm text-gray-500 dark:text-gray-400">
                              -
                            </td>
                          )}
                        </tr>
                        <tr>
                          <td className="px-3 sm:px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white transition-colors">Geçerlilik Süresi</td>
                          {selectedOffer?.relatedQuotes && Array.isArray(selectedOffer.relatedQuotes) && selectedOffer.relatedQuotes.length > 0 ? selectedOffer.relatedQuotes.map((quote) => {
                            return (
                              <td key={quote.quoteItemsId} className="px-3 sm:px-4 py-2 text-center text-sm text-gray-900 dark:text-white transition-colors">
                                {quote.validUntil ? `${quote.validUntil} gün` : (quote.validUntilDate ? formatDate(quote.validUntilDate) : '-')}
                              </td>
                            );
                          }) : (
                            <td className="px-3 sm:px-4 py-2 text-center text-sm text-gray-500 dark:text-gray-400">
                              -
                            </td>
                          )}
                        </tr>
                        <tr>
                          <td className="px-3 sm:px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white transition-colors">Ödeme Şartı</td>
                          {selectedOffer?.relatedQuotes && Array.isArray(selectedOffer.relatedQuotes) && selectedOffer.relatedQuotes.length > 0 ? selectedOffer.relatedQuotes.map((quote) => {
                            return (
                              <td key={quote.quoteItemsId} className="px-3 sm:px-4 py-2 text-center text-sm text-gray-900 dark:text-white transition-colors">
                                {quote.paymentTerm || '-'}
                              </td>
                            );
                          }) : (
                            <td className="px-3 sm:px-4 py-2 text-center text-sm text-gray-500 dark:text-gray-400">
                              -
                            </td>
                          )}
                        </tr>
                        <tr>
                          <td className="px-3 sm:px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white transition-colors">Tedarikçi Not</td>
                          {selectedOffer?.relatedQuotes && Array.isArray(selectedOffer.relatedQuotes) && selectedOffer.relatedQuotes.length > 0 ? selectedOffer.relatedQuotes.map((quote) => {
                            return (
                              <td key={quote.quoteItemsId} className="px-3 sm:px-4 py-2 text-center">
                                <div className="max-w-xs mx-auto">
                                  <div className="text-sm text-gray-900 dark:text-white transition-colors break-words">
                                    {quote.noteFromSupplier || '-'}
                                  </div>
                                </div>
                              </td>
                            );
                          }) : (
                            <td className="px-3 sm:px-4 py-2 text-center text-sm text-gray-500 dark:text-gray-400">
                              -
                            </td>
                          )}
                        </tr>
                        {isMuzakere && (
                          <>
                            <tr>
                              <td className="px-3 sm:px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white transition-colors">Hedef Miktar</td>
                              {selectedOffer?.relatedQuotes && Array.isArray(selectedOffer.relatedQuotes) && selectedOffer.relatedQuotes.length > 0 ? selectedOffer.relatedQuotes.map((quote) => {
                                const quoteId = quote.quoteItemsId?.toString() || '';
                                const isDisabled = !selectedSuppliers.includes(quoteId) || isPreviewMode;
                                // Use negotiateValues if exists, otherwise use offeredQuantity as default
                                const targetQuantity = negotiateValues[quoteId]?.targetQuantity ?? (quote.offeredQuantity ? Number(quote.offeredQuantity) : undefined);
                                return (
                                  <td key={quote.quoteItemsId} className="px-3 sm:px-4 py-2 text-center">
                                    <input 
                                      type="text" 
                                      placeholder="Miktar giriniz"
                                      value={targetQuantity ? Number(targetQuantity).toLocaleString('tr-TR') : ''}
                                      onChange={(e) => {
                                        const rawValue = e.target.value.replace(/\./g, '');
                                        if (rawValue === '') {
                                          handleNegotiateValueChange(quoteId, 'targetQuantity', 0);
                                        } else {
                                          const numValue = parseInt(rawValue);
                                          if (!isNaN(numValue) && numValue >= 0) {
                                            handleNegotiateValueChange(quoteId, 'targetQuantity', numValue);
                                          }
                                        }
                                      }}
                                      onFocus={(e) => {
                                        e.target.select();
                                        // Show raw number when focused for easier editing
                                        const rawValue = targetQuantity || '';
                                        e.target.value = rawValue.toString();
                                      }}
                                      onBlur={(e) => {
                                        // Format the number when losing focus
                                        const rawValue = e.target.value.replace(/\./g, '');
                                        const numValue = parseInt(rawValue);
                                        if (!isNaN(numValue) && numValue >= 0) {
                                          e.target.value = Number(numValue).toLocaleString('tr-TR');
                                        }
                                      }}
                                      disabled={isDisabled}
                                      className={`w-32 px-3 py-2 text-sm border rounded focus:ring-2 focus:ring-primary focus:border-transparent ${
                                        !isDisabled
                                          ? 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white'
                                          : 'border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                                      }`}
                                    />
                                  </td>
                                );
                              }) : (
                                <td className="px-3 sm:px-4 py-2 text-center text-sm text-gray-500 dark:text-gray-400">
                                  -
                                </td>
                              )}
                            </tr>
                            <tr>
                              <td className="px-3 sm:px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white transition-colors">Hedef Fiyat</td>
                              {selectedOffer?.relatedQuotes && Array.isArray(selectedOffer.relatedQuotes) && selectedOffer.relatedQuotes.length > 0 ? selectedOffer.relatedQuotes.map((quote) => {
                                const quoteId = quote.quoteItemsId?.toString() || '';
                                const isDisabled = !selectedSuppliers.includes(quoteId) || isPreviewMode;
                                return (
                                  <td key={quote.quoteItemsId} className="px-3 sm:px-4 py-2 text-center">
                                    <PriceInput
                                      value={negotiateValues[quoteId]?.targetPrice || 0}
                                      onChange={(value) => {
                                        handleNegotiateValueChange(quoteId, 'targetPrice', value);
                                      }}
                                      onFocus={(e) => e.target.select()}
                                      className={`w-32 px-3 py-2 text-sm border rounded focus:ring-2 focus:ring-primary focus:border-transparent ${
                                        !isDisabled
                                          ? 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white'
                                          : 'border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                                      }`}
                                      disabled={isDisabled}
                                      placeholder={quote.currency || ''}
                                      allowNegative={false}
                                      decimalScale={4}
                                      fixedDecimalScale={true}
                                    />
                                  </td>
                                );
                              }) : (
                                <td className="px-3 sm:px-4 py-2 text-center text-sm text-gray-500 dark:text-gray-400">
                                  -
                                </td>
                              )}
                            </tr>
                            <tr>
                              <td className="px-3 sm:px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white transition-colors">Hedef Teslim Süresi</td>
                              {selectedOffer?.relatedQuotes && Array.isArray(selectedOffer.relatedQuotes) && selectedOffer.relatedQuotes.length > 0 ? selectedOffer.relatedQuotes.map((quote) => {
                                const quoteId = quote.quoteItemsId?.toString() || '';
                                const isDisabled = !selectedSuppliers.includes(quoteId) || isPreviewMode;
                                return (
                                  <td key={quote.quoteItemsId} className="px-3 sm:px-4 py-2 text-center">
                                    <input 
                                      type="number" 
                                      placeholder="Gün"
                                      value={negotiateValues[quoteId]?.targetDeliveryDays || ''}
                                      onChange={(e) => handleNegotiateValueChange(quoteId, 'targetDeliveryDays', Number(e.target.value))}
                                      onFocus={(e) => {
                                        setTimeout(() => {
                                          e.target.select();
                                        }, 0);
                                      }}
                                      disabled={isDisabled}
                                      className={`w-32 px-3 py-2 text-sm border rounded focus:ring-2 focus:ring-primary focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                                        !isDisabled
                                          ? 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white'
                                          : 'border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                                      }`}
                                    />
                                  </td>
                                );
                              }) : (
                                <td className="px-3 sm:px-4 py-2 text-center text-sm text-gray-500 dark:text-gray-400">
                                  -
                                </td>
                              )}
                            </tr>
                          </>
                        )}
                      </tbody>
                    </table>
                        </div>

                        {/* Mobile Cards */}
                        <div className="lg:hidden space-y-3 p-2">
                          {selectedOffer?.relatedQuotes && Array.isArray(selectedOffer.relatedQuotes) && selectedOffer.relatedQuotes.length > 0 ? selectedOffer.relatedQuotes.map((quote) => {
                            const quoteId = quote.quoteItemsId?.toString() || '';
                            const isChecked = isMuzakere ? selectedSuppliers.includes(quoteId) : selectedSupplier === quoteId;
                            const hasPrice = !hasEmptyPrice(quote);
                            // Use negotiateValues if exists, otherwise use offeredQuantity as default
                            const targetQuantity = negotiateValues[quoteId]?.targetQuantity ?? (quote.offeredQuantity ? Number(quote.offeredQuantity) : undefined);
                            
                            return (
                              <div key={quote.quoteItemsId} className={`bg-gray-50 dark:bg-gray-700 rounded-lg p-3 border border-gray-200 dark:border-gray-600 ${!hasPrice ? 'opacity-60' : ''}`}>
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center flex-1">
                                    <div className="w-6 h-6 rounded-full flex items-center justify-center mr-2 bg-blue-100 dark:bg-blue-900/30">
                                      <i className="fas fa-building text-xs text-blue-600"></i>
                                    </div>
                                    <div className="flex-1">
                                      <h4 className="text-sm font-medium text-gray-900 dark:text-white flex items-center flex-wrap gap-2">
                                        {quote.supplierName || 'Tedarikçi'}
                                        {quote.round != null && (
                                          <span className="text-[10px] text-gray-500 dark:text-gray-400 font-bold">Tur {quote.round}</span>
                                        )}
                                        {quote.isNegotiate && (
                                          <span className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-[10px] font-bold rounded border border-gray-200 dark:border-gray-600 uppercase tracking-tighter">
                                            Müzakere
                                          </span>
                                        )}
                                        {quote.quoteStatus === 'Closed' && (
                                          <span
                                            className="px-1.5 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-[10px] font-bold rounded border border-red-200 dark:border-red-800 uppercase tracking-tighter"
                                            title="Bu teklif için tedarikçinin yanıt süresi dolduğundan teklif sistem tarafından otomatik kapatılmıştır."
                                          >
                                            Süresi Geçmiş
                                          </span>
                                        )}
                                        {!hasPrice && <span className="text-xs text-red-500">(Fiyat yok)</span>}
                                      </h4>
                                      <span className="mt-1 flex items-center gap-1">
                                        <button
                                          onClick={() => openHistoryModal(quote.quoteItemsId || 0, quote.supplierName || 'Tedarikçi')}
                                          className="text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                                          title="Geçmiş"
                                        >
                                          <i className="fas fa-history text-xs"></i>
                                        </button>
                                        <button
                                          onClick={() => quote.quoteItemsId && !isPreviewMode && openQuoteItemDeleteConfirmation(quote.quoteItemsId, quote.supplierName || 'Tedarikçi')}
                                          className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:text-gray-400 dark:disabled:text-gray-500"
                                          title="Teklif kalemini sil"
                                          disabled={isPreviewMode}
                                        >
                                          <i className="fas fa-trash-alt text-xs"></i>
                                        </button>
                                      </span>
                                    </div>
                                  </div>
                                  <input 
                                    type="checkbox" 
                                    checked={isChecked}
                                    onChange={() => !isPreviewMode && handleSupplierSelection(quoteId)}
                                    disabled={!hasPrice || isPreviewMode}
                                    title={isPreviewMode ? 'Önizleme modu' : !hasPrice ? 'Fiyat bilgisi olmayan teklif seçilemez' : ''}
                                    className={`w-4 h-4 text-blue-600 focus:ring-blue-500 bg-gray-50 dark:bg-gray-700 ${
                                      hasPrice && !isPreviewMode ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
                                    }`}
                                  />
                                </div>
                                
                                <div className="space-y-2 text-xs">
                                  <div className="flex justify-between">
                                    <span className="font-medium text-gray-500 dark:text-gray-400">Miktar:</span>
                                    <span className="text-gray-700 dark:text-gray-300">
                                      {quote.offeredQuantity ? Number(quote.offeredQuantity).toLocaleString('tr-TR') : '-'}
                                    </span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="font-medium text-gray-500 dark:text-gray-400">Fiyat:</span>
                                    <span className="text-gray-700 dark:text-gray-300">
                                      {quote.offeredPrice ? (
                                        <div>
                                          <div className="flex items-center gap-1.5">
                                            <PriceInput
                                              value={parseFloat(quote.offeredPrice)}
                                              onChange={() => {}}
                                              currency={quote.currency || ''}
                                              disabled={true}
                                              decimalScale={4}
                                              fixedDecimalScale={true}
                                              className="text-xs bg-transparent border-none p-0 cursor-default"
                                            />
                                            {quote.vatIncluded ? (
                                              <span className="text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
                                                (KDV dahil {quote.vatRate || 0}%)
                                              </span>
                                            ) : (
                                              <span className="text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
                                                (+KDV {quote.vatRate || 0}%)
                                              </span>
                                            )}
                                          </div>
                                          {quote.offeredPriceUSD != null && (
                                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 text-right">
                                              <PriceInput
                                                value={parseFloat(quote.offeredPriceUSD)}
                                                onChange={() => {}}
                                                currency="USD"
                                                disabled={true}
                                                decimalScale={4}
                                                fixedDecimalScale={true}
                                                className="text-xs bg-transparent border-none p-0 cursor-default"
                                              />
                                            </div>
                                          )}
                                        </div>
                                      ) : '-'}
                                    </span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="font-medium text-gray-500 dark:text-gray-400">Teslim Süresi:</span>
                                    <span className="text-gray-700 dark:text-gray-300">
                                      {quote.offeredDeliveryDays ? `${quote.offeredDeliveryDays} gün` : '-'}
                                    </span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="font-medium text-gray-500 dark:text-gray-400">Geçerlilik Süresi:</span>
                                    <span className="text-gray-700 dark:text-gray-300">
                                      {quote.validUntil ? `${quote.validUntil} gün` : (quote.validUntilDate ? formatDate(quote.validUntilDate) : '-')}
                                    </span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="font-medium text-gray-500 dark:text-gray-400">Ödeme Şartı:</span>
                                    <span className="text-gray-700 dark:text-gray-300 text-right max-w-32 truncate">
                                      {quote.paymentTerm || '-'}
                                    </span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="font-medium text-gray-500 dark:text-gray-400">Not:</span>
                                    <span className="text-gray-700 dark:text-gray-300 text-right max-w-32 truncate">
                                      {quote.noteFromSupplier || '-'}
                                    </span>
                                  </div>
                                </div>

                                {/* Müzakere Inputs */}
                                {isMuzakere && (
                                  <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-600 space-y-2">
                                    <div className="flex justify-between items-center">
                                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Hedef Miktar:</span>
                                      <input 
                                        type="text" 
                                        placeholder="Miktar"
                                        value={targetQuantity ? Number(targetQuantity).toLocaleString('tr-TR') : ''}
                                        onChange={(e) => {
                                          const rawValue = e.target.value.replace(/\./g, '');
                                          if (rawValue === '') {
                                            handleNegotiateValueChange(quoteId, 'targetQuantity', 0);
                                          } else {
                                            const numValue = parseInt(rawValue);
                                            if (!isNaN(numValue) && numValue >= 0) {
                                              handleNegotiateValueChange(quoteId, 'targetQuantity', numValue);
                                            }
                                          }
                                        }}
                                        onFocus={(e) => {
                                          e.target.select();
                                          const rawValue = targetQuantity || 0;
                                          e.target.value = rawValue.toString();
                                        }}
                                        onBlur={(e) => {
                                          const rawValue = e.target.value.replace(/\./g, '');
                                          const numValue = parseInt(rawValue);
                                          if (!isNaN(numValue) && numValue >= 0) {
                                            e.target.value = Number(numValue).toLocaleString('tr-TR');
                                          }
                                        }}
                                        disabled={!selectedSuppliers.includes(quoteId) || isPreviewMode}
                                        className={`w-24 px-2 py-1 text-xs border rounded focus:ring-1 focus:ring-primary focus:border-transparent ${
                                          selectedSuppliers.includes(quoteId) && !isPreviewMode
                                            ? 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white'
                                            : 'border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                                        }`}
                                      />
                                    </div>
                                    <div className="flex justify-between items-center">
                                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Hedef Fiyat:</span>
                                      <PriceInput
                                        value={negotiateValues[quoteId]?.targetPrice || 0}
                                        onChange={(value) => {
                                          handleNegotiateValueChange(quoteId, 'targetPrice', value);
                                        }}
                                        onFocus={(e) => e.target.select()}
                                        className={`w-24 px-2 py-1 text-xs border rounded focus:ring-1 focus:ring-primary focus:border-transparent ${
                                          selectedSuppliers.includes(quoteId) && !isPreviewMode
                                            ? 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white'
                                            : 'border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                                        }`}
                                        disabled={!selectedSuppliers.includes(quoteId) || isPreviewMode}
                                        placeholder={quote.currency || ''}
                                        allowNegative={false}
                                        decimalScale={4}
                                        fixedDecimalScale={true}
                                      />
                                    </div>
                                    <div className="flex justify-between items-center">
                                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Hedef Teslim:</span>
                                      <input 
                                        type="number" 
                                        placeholder="Gün"
                                        value={negotiateValues[quoteId]?.targetDeliveryDays || ''}
                                        onChange={(e) => handleNegotiateValueChange(quoteId, 'targetDeliveryDays', Number(e.target.value))}
                                        onFocus={(e) => {
                                          setTimeout(() => {
                                            e.target.select();
                                          }, 0);
                                        }}
                                        disabled={!selectedSuppliers.includes(quoteId) || isPreviewMode}
                                        className={`w-24 px-2 py-1 text-xs border rounded focus:ring-1 focus:ring-primary focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                                          selectedSuppliers.includes(quoteId) && !isPreviewMode
                                            ? 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white'
                                            : 'border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                                        }`}
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          }) : (
                            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                              <i className="fas fa-exclamation-triangle text-2xl mb-2"></i>
                              <p className="text-sm">Teklif Bulunamadı</p>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>

                

                {/* Purchase History Section */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden mb-3 sm:mb-4 transition-colors duration-300">
                  <div className="p-2 sm:p-3 border-b border-gray-200 dark:border-gray-700 transition-colors duration-300">
                    <h3 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white transition-colors">Satın Alma Geçmişi</h3>
                  </div>
                  <div className="overflow-x-auto">
                    {/* Desktop Table */}
                    <div className="hidden lg:block">
                    <table className="w-full min-w-[600px]">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase transition-colors whitespace-nowrap">TARİH</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase transition-colors whitespace-nowrap">MİKTAR</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase transition-colors whitespace-nowrap">BİRİM FİYAT</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase transition-colors whitespace-nowrap">PARA BİRİMİ</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase transition-colors whitespace-nowrap">$ DEĞERİ</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase transition-colors whitespace-nowrap">SATICI FİRMA</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {isPurchaseHistoryLoading ? (
                          <tr>
                            <td colSpan={6} className="px-3 py-6 text-center text-gray-500 dark:text-gray-400">
                              <div className="flex items-center justify-center">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-2"></div>
                                Satın alma geçmişi yükleniyor...
                              </div>
                            </td>
                          </tr>
                        ) : purchaseHistory && purchaseHistory.length > 0 ? (
                          purchaseHistory.map((item: any, index: number) => (
                            <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                              <td className="px-3 py-2 text-sm text-gray-900 dark:text-white">
                                {item.purchaseDate ? formatDate(item.purchaseDate) : '-'}
                              </td>
                              <td className="px-3 py-2 text-sm text-gray-900 dark:text-white">
                                {item.quantity ? Number(item.quantity).toLocaleString('tr-TR') : '-'}
                              </td>
                              <td className="px-3 py-2 text-sm text-gray-900 dark:text-white">
                                {item.unitPrice ? `${parseFloat(item.unitPrice).toFixed(3).replace('.', ',')}` : '-'}
                              </td>
                              <td className="px-3 py-2 text-sm text-gray-900 dark:text-white">
                                {item.currency || '-'}
                              </td>
                              <td className="px-3 py-2 text-sm text-gray-900 dark:text-white">
                                {item.unitPriceUSDValue ? `${parseFloat(item.unitPriceUSDValue).toFixed(3).replace('.', ',')}` : '-'}
                              </td>
                              <td className="px-3 py-2 text-sm text-gray-900 dark:text-white">
                                {item.supplierName || '-'}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={6} className="px-3 py-6 text-center text-gray-500 dark:text-gray-400">
                              Satın alma geçmişi verisi bulunamadı
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                    </div>

                    {/* Mobile Cards */}
                    <div className="lg:hidden space-y-3 p-3">
                      {isPurchaseHistoryLoading ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="flex items-center">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-2"></div>
                            <span className="text-sm text-gray-500 dark:text-gray-400">Satın alma geçmişi yükleniyor...</span>
                          </div>
                        </div>
                      ) : purchaseHistory && purchaseHistory.length > 0 ? (
                        purchaseHistory.map((item: any, index: number) => (
                          <div key={index} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                            <div className="space-y-2 text-xs">
                              <div className="flex justify-between">
                                <span className="font-medium text-gray-500 dark:text-gray-400">Tarih:</span>
                                <span className="text-gray-700 dark:text-gray-300">
                                  {item.purchaseDate ? formatDate(item.purchaseDate) : '-'}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="font-medium text-gray-500 dark:text-gray-400">Miktar:</span>
                                <span className="text-gray-700 dark:text-gray-300">
                                  {item.quantity ? Number(item.quantity).toLocaleString('tr-TR') : '-'}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="font-medium text-gray-500 dark:text-gray-400">Birim Fiyat:</span>
                                <span className="text-gray-700 dark:text-gray-300">
                                  {item.unitPrice ? `${parseFloat(item.unitPrice).toFixed(3).replace('.', ',')}` : '-'}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="font-medium text-gray-500 dark:text-gray-400">Para Birimi:</span>
                                <span className="text-gray-700 dark:text-gray-300">
                                  {item.currency || '-'}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="font-medium text-gray-500 dark:text-gray-400">$ Değeri:</span>
                                <span className="text-gray-700 dark:text-gray-300">
                                  {item.unitPriceUSDValue ? `${parseFloat(item.unitPriceUSDValue).toFixed(3).replace('.', ',')}` : '-'}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="font-medium text-gray-500 dark:text-gray-400">Satıcı Firma:</span>
                                <span className="text-gray-700 dark:text-gray-300 text-right max-w-32 truncate">
                                  {item.supplierName || '-'}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                          <i className="fas fa-inbox text-2xl mb-2"></i>
                          <p className="text-sm">Satın alma geçmişi verisi bulunamadı</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
              </div>
            </div>
          </div>
        )}

        {/* ItemView Modal */}
        {showItemViewModal && itemToView && (
          <ItemView
            isOpen={showItemViewModal}
            onClose={closeItemViewModal}
            itemId={itemToView.itemId}
            mode="view"
          />
        )}

        {/* Quote Item History Modal */}
        {showHistoryModal && selectedQuoteItemId && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                closeHistoryModal();
              }
            }}
          >
            <div 
              className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl mx-auto max-h-[90vh] overflow-y-auto transition-colors duration-300"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white transition-colors">
                      Teklif Geçmişi
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {selectedQuoteItemSupplierName} - {selectedOffer?.productName || 'Ürün'}
                    </p>
                  </div>
                  <button
                    onClick={closeHistoryModal}
                    className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  >
                    <i className="fas fa-times text-xl"></i>
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-4 sm:p-6">
                {isQuoteItemHistoryLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                      <p className="text-gray-500 dark:text-gray-400 text-sm">Teklif geçmişi yükleniyor...</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {(() => {
                      if (!quoteItemHistory || quoteItemHistory.length === 0) {
                        return (
                          <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                            <i className="fas fa-inbox text-4xl mb-4"></i>
                            <p className="text-sm">Teklif geçmişi bulunmamaktadır.</p>
                          </div>
                        );
                      }

                      // Reverse the array to show newest items first
                      const reversedHistory = [...quoteItemHistory].reverse();

                      return (
                        <div className="space-y-3">
                          {reversedHistory.map((historyItem, index) => (
                            <div
                              key={index}
                              className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600 transition-colors hover:shadow-md"
                            >
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex flex-col gap-1">
                                  <div className="flex items-center space-x-2">
                                    <div className="w-2 h-2 rounded-full bg-blue-500 dark:bg-blue-400"></div>
                                    <span className="text-sm font-semibold text-gray-900 dark:text-white">
                                      {historyItem.step || 'Bilinmeyen Adım'}
                                    </span>
                                  </div>
                                  {historyItem.userName && !(historyItem.step?.includes('Satıcı') ?? false) && (
                                    <span className="text-xs text-gray-500 dark:text-gray-400 ml-4">
                                      Kullanıcı: {historyItem.userName}
                                    </span>
                                  )}
                                </div>
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {historyItem.stageDate ? formatDate(historyItem.stageDate) : '-'}
                                </span>
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mt-3">
                                <div>
                                  <span className="text-xs text-gray-600 dark:text-gray-400 block mb-1">Miktar</span>
                                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                                    {historyItem.quantity ? Number(historyItem.quantity).toLocaleString('tr-TR') : '0'} {selectedOffer?.unit || 'adet'}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-xs text-gray-600 dark:text-gray-400 block mb-1">Birim Fiyat</span>
                                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                                    {(historyItem.price || 0).toLocaleString('tr-TR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} {historyItem.currency || 'TRY'}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-xs text-gray-600 dark:text-gray-400 block mb-1">Teslimat (Gün)</span>
                                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                                    {historyItem.deliveryDays || 0} gün
                                  </span>
                                </div>
                                <div>
                                  <span className="text-xs text-gray-600 dark:text-gray-400 block mb-1">Toplam Tutar</span>
                                  <span className="text-sm font-bold text-gray-900 dark:text-white">
                                    {((historyItem.quantity || 0) * (historyItem.price || 0)).toLocaleString('tr-TR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} {historyItem.currency || 'TRY'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Netsis'te bugünün kuru yoksa kur soran pencere */}
        {ratePrompt && (
          <ExchangeRatePrompt
            info={ratePrompt.info}
            onSubmit={handleRatePromptSubmit}
            onSkip={handleRatePromptSkip}
          />
        )}

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          isOpen={showDeleteConfirmation}
          onClose={() => {
            setShowDeleteConfirmation(false);
            setOfferToDelete(null);
          }}
          onConfirm={confirmDelete}
          title={offerToDelete ? "Talebi ve Teklifleri Sil" : "Talepten Vazgeç"}
          message={offerToDelete 
            ? `${offerToDelete.requestNo} numaralı talebi ve bu talebe ait tüm teklif öğelerini silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`
            : "Bu talebi iptal etmek ve silmek istediğinizden emin misiniz? Bu işlem geri alınamaz."
          }
          confirmText="Evet, Sil"
          cancelText="İptal"
          type="danger"
          icon="fas fa-trash"
        />

        {/* Quote Item Delete Confirmation Modal (Kıyaslama modalında teklif kalemi sil) */}
        <ConfirmationModal
          isOpen={showQuoteItemDeleteConfirmation}
          onClose={() => {
            setShowQuoteItemDeleteConfirmation(false);
            setQuoteItemToDelete(null);
          }}
          onConfirm={confirmQuoteItemDelete}
          title="Teklif Kalemini Sil"
          message={quoteItemToDelete
            ? `"${quoteItemToDelete.supplierName}" teklif kalemini silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`
            : "Bu teklif kalemini silmek istediğinize emin misiniz?"
          }
          confirmText="Evet, Sil"
          cancelText="İptal"
          type="danger"
          icon="fas fa-trash-alt"
          disabled={isQuoteItemDeleteLoading}
        />
      </div>
    </Layout>
  );
};

export default QuoteEvaluationPage;
